import dynamic from "next/dynamic";

import { useMemo } from "react";

import { Loader } from "components/customElements/loader";
import { colors } from "consts";

import ToggleTimeScope from "./ToggleTimeScope";
import useFinancialData from "./useFinancialData";

const TimeSeriesChart = dynamic(
  () => import("components/AmChart/TimeSeriesChart"),
  {
    ssr: false,
  }
);

const marginsChartConfig = {
  title: "Margins",
  charts: [
    {
      type: "line",
      name: "Gross Margin",
      fieldName: "gross_margin",
      color: colors.smartTeal,
    },
    {
      type: "line",
      name: "EBITDA Margin",
      fieldName: "ebitda_margin",
      color: colors.grass,
    },
    {
      type: "line",
      name: "Net Margin",
      fieldName: "net_margin",
      color: colors.mellowYellow,
    },
  ],
};

const returnsChartConfig = {
  title: "Returns",
  charts: [
    {
      type: "line",
      name: "ROA",
      fieldName: "roa",
      color: colors.smartTeal,
    },
    {
      type: "line",
      name: "ROC",
      fieldName: "rotac",
      color: colors.grass,
    },
    {
      type: "line",
      name: "ROCE",
      fieldName: "roce",
      color: colors.mellowYellow,
    },
  ],
};

const dupontChartConfig = {
  title: "Dupont",
  charts: [
    {
      type: "line",
      name: "Dupont",
      fieldName: "dupont",
      color: colors.smartTeal,
    },
    {
      type: "line",
      name: "Asset Turnover",
      fieldName: "asset_turnover",
      color: colors.grass,
    },
    {
      type: "line",
      name: "Equity Multiplier",
      fieldName: "equity_multiplier",
      color: colors.mellowYellow,
    },
  ],
  isLineValuePercent: false,
};

interface Props {
  entity: any;
}

const Profitability = ({ entity }: Props) => {
  const { timeScopeData: chartData, isLoading } = useFinancialData(
    entity?.isin || ""
  );

  const hasMarginsData = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.gross_margin || !!item?.ebitda_margin || !!item?.net_margin
      ),
    [chartData]
  );

  const hasReturnsData = useMemo(
    () =>
      chartData?.some((item) => !!item?.roa || !!item?.rotac || !!item?.roce),
    [chartData]
  );

  const hasDupontData = useMemo(
    () =>
      chartData?.some(
        (item) =>
          !!item?.dupont || !!item?.asset_turnover || !!item?.equity_multiplier
      ),
    [chartData]
  );

  if (isLoading) return <Loader />;

  const chartClass = "w-full h-[400px] mb-[80px]";
  return (
    <>
      <ToggleTimeScope />
      {hasMarginsData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={marginsChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasReturnsData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={returnsChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
      {hasDupontData ? (
        <div className={chartClass}>
          <TimeSeriesChart
            chartData={chartData}
            chartConfig={dupontChartConfig}
            className={chartClass}
          />
        </div>
      ) : null}
    </>
  );
};

export default Profitability;
